import React, { useId } from "react"

import { ResolvedSelect } from "../../../components/Select/ResolvedSelect.tsx"
import { cn } from "../../../utils/cn.ts"
import type { FacetOption } from "../redux/global-map.types.ts"
import { FILTER_PILL_CLASS } from "./filter-pill.ts"

interface FacetMultiSelectFilterProps {
  label: string
  options: FacetOption[]
  values: (string | number)[]
  onChange: (values: string[]) => void
  icon?: React.ReactNode
  className?: string
}

// The status facet's own value shape (`GlobalMapFilterBar`'s
// `handleStatusChange` produces/consumes these directly) — every other
// facet's options have `entity: undefined` and fall back to a plain
// `String(value)`.
function optionSelectValue(option: FacetOption): string {
  return option.entity
    ? `${option.entity}:${option.value}`
    : String(option.value)
}

/**
 * A multi-select, searchable dropdown over a facet's option list (id + label
 * + count) — status/project type both read `GlobalMapFilters`' own array
 * fields (`projectStatusIds`/`leadStatusIds`/`projectTypeIds`), so unlike
 * `RelationshipFilter`'s single-id pickers this is deliberately multi-select
 * (antd's own `mode="multiple"`), preserving the existing "select several
 * statuses/types at once" behavior the earlier `PillGroup` toggle buttons
 * had. Reuses the same `ResolvedSelect` primitive and rendering convention
 * (facet label + count, null-label fallback to the raw value)
 * `RelationshipFilter` already established, so every picker in the bar now
 * reads as one dropdown family.
 *
 * The backend's status facet can legitimately return two entries sharing one
 * numeric `value` under different labels (Lead/Project status enums overlap
 * by coincidence — `GlobalMapFilterBar`'s own module docstring covers this).
 * Real, reported bug fix: this used to key every option's antd `value` on
 * nothing but `String(value)`, so checking "Planning" (project, value 1) and
 * "New" (lead, value 1) were indistinguishable to antd's own selection
 * state — checking one visually checked both. Each status option now also
 * carries `entity` (`FacetOption.entity`), and `optionSelectValue` folds it
 * into the antd `value` (`"project:1"` vs `"lead:1"`) so two same-numbered
 * options are genuinely distinct selections; every other facet's options
 * have no `entity` and keep the plain `String(value)` shape unchanged. The
 * `key` stays independently distinct from `value` for the same reason as
 * before (`PillGroup`'s old `key={`${value}::${label}`}` fix) — a `value`
 * collision alone, before this fix, was already enough to confuse antd even
 * before selection state entered the picture.
 */
export function FacetMultiSelectFilter({
  label,
  options,
  values,
  onChange,
  icon,
  className
}: FacetMultiSelectFilterProps) {
  const inputId = useId()

  const selectOptions = options.map((option) => {
    const optionLabel =
      option.label != null ? option.label : String(option.value)
    return {
      key: `${option.value}::${optionLabel}`,
      value: optionSelectValue(option),
      label: option.count ? `${optionLabel} (${option.count})` : optionLabel
    }
  })

  return (
    <ResolvedSelect
      id={inputId}
      aria-label={label}
      mode="multiple"
      allowClear
      showSearch
      prefix={icon}
      placeholder={label}
      options={selectOptions}
      value={values.map(String)}
      onChange={(next) => onChange((next as string[] | undefined) ?? [])}
      optionFilterProp="label"
      filterOption={(input, option) =>
        ((option?.label as string) ?? "")
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      // Caps the trigger at zero visible value tags — the closed control
      // shows only "Label (N)" (via `maxTagPlaceholder` below), never
      // per-value tags — so the pill always stays one line at a fixed
      // height regardless of how many options are selected. A real bug
      // motivated the cap itself, not just a style preference: with no cap
      // at all, two-plus selections wrapped onto a second line inside this
      // fixed-width pill and visually overlapped whatever rendered directly
      // below the filter bar.
      maxTagCount={0}
      maxTagPlaceholder={(omitted) =>
        omitted?.length ? `${label} (${omitted.length})` : null
      }
      className={cn("w-[170px]", FILTER_PILL_CLASS, className)}
    />
  )
}

export default FacetMultiSelectFilter
